import { useState, useRef, useEffect } from 'react'
import { useStore } from '../store/useStore'
import { PageHeader, Card, Button, UpgradePrompt } from '../components/UI'
import { aiService, buildSystemPrompt } from '../services/ai'

const C = { mint: '#00C896', text: '#E8F5EE', muted: '#6B8F7A', border: '#1E2E24', surfaceAlt: '#161F1A', red: '#FF4D6A' }

const SUGGESTED = [
  "Am I on track to retire comfortably?",
  "How can I reduce my food spending?",
  "Should I pay off debt or invest?",
  "How much should I have in my emergency fund?",
]

// Mock user context — in production, build from real account/transaction data
const getUserContext = (user, plan) => ({
  userId: user?.id,
  plan,
  netWorth: 84320,
  monthlyIncome: 7200,
  monthlySpend: 4830,
  savingsRate: 32.9,
  creditScore: 748,
  topCategories: [
    { name: 'Housing', amount: 1800, budget: 1800 },
    { name: 'Food & Dining', amount: 620, budget: 500 },
    { name: 'Transport', amount: 210, budget: 250 },
  ],
})

export default function AICoach() {
  const { user, plan, canUseFeature } = useStore()
  const [messages, setMessages] = useState([
    { role: 'ai', text: "Hi! I'm your PeppermintFi AI Coach, powered by Claude. I have access to your financial snapshot and I'm here to give you personalized, actionable advice. What's on your mind?" }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [queriesLeft, setQueriesLeft] = useState(plan === 'free' ? 5 : plan === 'pro' ? 20 : Infinity)
  const bottomRef = useRef(null)

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  if (!canUseFeature('ai_coach')) {
    return (
      <div>
        <PageHeader title="AI Coach" subtitle="Your personal finance advisor" />
        <UpgradePrompt feature="AI Coach" requiredPlan="Pro" />
      </div>
    )
  }

  const send = async (text) => {
    if (!text.trim() || loading) return
    if (queriesLeft === 0) return

    const userMsg = text.trim()
    setMessages(m => [...m, { role: 'user', text: userMsg }])
    setInput('')
    setLoading(true)
    setQueriesLeft(q => Math.max(0, q - 1))

    try {
      const history = messages.map(m => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text }))
      const userContext = getUserContext(user, plan)

      // Direct Claude API call (for MVP — move to backend for production)
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: buildSystemPrompt(userContext),
          messages: [...history, { role: 'user', content: userMsg }],
        }),
      })
      const data = await response.json()
      const reply = data.content?.[0]?.text || 'Sorry, I had trouble processing that. Please try again.'
      setMessages(m => [...m, { role: 'ai', text: reply }])
    } catch {
      setMessages(m => [...m, { role: 'ai', text: 'Connection issue. Please try again in a moment.' }])
    }
    setLoading(false)
  }

  return (
    <div>
      <PageHeader
        title="AI Coach"
        subtitle="Personalized financial advice based on your real data"
        action={
          plan !== 'premium' && (
            <span style={{ color: C.muted, fontSize: 13 }}>
              {queriesLeft === Infinity ? '∞' : queriesLeft} queries left this month
              {' · '}<a href="/pricing" style={{ color: C.mint, textDecoration: 'none' }}>Upgrade</a>
            </span>
          )
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', maxHeight: 700 }}>
        <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
          {/* Chat header */}
          <div style={{ padding: '18px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: 'rgba(0,200,150,0.12)',
              border: '1px solid rgba(0,200,150,0.25)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: C.mint, fontSize: 18,
            }}>✦</div>
            <div>
              <div style={{ color: C.text, fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14 }}>PeppermintFi AI Coach</div>
              <div style={{ color: C.mint, fontSize: 11 }}>● Powered by Claude · Knows your finances</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '75%', padding: '13px 17px',
                  borderRadius: m.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  background: m.role === 'user' ? C.mint : C.surfaceAlt,
                  border: `1px solid ${m.role === 'user' ? C.mint : C.border}`,
                  color: m.role === 'user' ? '#000' : C.text,
                  fontSize: 14, lineHeight: 1.65,
                }}>{m.text}</div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex' }}>
                <div style={{ padding: '13px 18px', background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: '18px 18px 18px 4px', display: 'flex', gap: 5 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: C.mint, animation: 'pulse 1.2s infinite', animationDelay: `${i*0.2}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggested prompts */}
          {messages.length === 1 && (
            <div style={{ padding: '0 20px 16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SUGGESTED.map(s => (
                <button key={s} onClick={() => send(s)} style={{
                  background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 99,
                  padding: '7px 14px', color: C.muted, fontSize: 12, cursor: 'pointer',
                  transition: 'border-color 0.15s', fontFamily: 'DM Sans, sans-serif',
                }}>{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{ padding: 16, borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10 }}>
            {queriesLeft === 0 ? (
              <div style={{ flex: 1, textAlign: 'center', padding: '12px', color: C.muted, fontSize: 13 }}>
                Monthly limit reached. <a href="/pricing" style={{ color: C.mint, textDecoration: 'none' }}>Upgrade for more →</a>
              </div>
            ) : (
              <>
                <input
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send(input)}
                  placeholder="Ask about your finances..."
                  style={{
                    flex: 1, background: C.surfaceAlt, border: `1px solid ${C.border}`,
                    borderRadius: 12, padding: '12px 16px', color: C.text, fontSize: 14,
                    outline: 'none', fontFamily: 'DM Sans, sans-serif',
                  }}
                />
                <Button onClick={() => send(input)} disabled={loading || !input.trim()}>Send</Button>
              </>
            )}
          </div>
        </Card>
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:1} }`}</style>
    </div>
  )
}
